/**
 * Grille de calendrier partagée (plan F1) — mois (42 jours) ou semaine (7 jours), calée sur le
 * lundi. La règle d'affichage (quelles tâches vivent sur quel jour) reste au parent
 * (`itemsForDay`) : `PlanningView` et `OverviewCalendar` n'ont pas la même définition.
 *
 * Ce n'est PAS une planche (`@exidis/ui` 1.5.0, reference.md §11.7) : un calendrier découpe une
 * donnée continue, ses jours sont des cases de tableau, pas des objets. Il ne prend donc ni
 * `.cells` ni `.cell` — en planche B, `.cells` pose une gouttière de 16 px entre les jours et
 * `.cell` un cadre complet par jour. Traits de tableau à la place : le conteneur porte le haut
 * et la gauche, chaque case sa droite et son bas, un seul trait de 2 px entre deux cases.
 * Aujourd'hui : filet 2 px au trait d'accent (`bg-accent-trait` — l'encre en clair, @exidis/ui
 * 1.7.0 : l'orchidée ne tient pas 3:1 en filet) posé DANS la case (`absolute`), pas une bordure
 * en plus qui la ferait grandir de 2 px.
 */

import { useTranslation } from 'react-i18next';
import { buildMonthGrid, buildWeekGrid, toISO, weekdayAbbreviations } from '../lib/calendar.js';
import { BTN_GHOST_SM } from '../ui/theme.js';

export function CalendarGrid({
  mode = 'month',
  anchor,
  today,
  locale,
  onShift,
  itemsForDay,
  renderItem,
  onDayClick,
  maxPerDay = 3,
  title,
  prevLabel,
  nextLabel,
}) {
  const { t } = useTranslation();
  const [year, month] = anchor.split('-').map(Number);
  const days = mode === 'week' ? buildWeekGrid(anchor) : buildMonthGrid(year, month - 1);
  const weekdays = weekdayAbbreviations(locale);
  const anchorMonth = month - 1;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        {title}
        <div className="flex gap-2">
          <button
            type="button"
            className={BTN_GHOST_SM}
            aria-label={prevLabel}
            onClick={() => onShift(-1)}
          >
            ‹
          </button>
          <button
            type="button"
            className={BTN_GHOST_SM}
            aria-label={nextLabel}
            onClick={() => onShift(1)}
          >
            ›
          </button>
        </div>
      </div>

      <div
        className={`grid grid-cols-7 border-l-2 border-t-2 border-line ${mode === 'week' ? 'min-h-48' : ''}`}
      >
        {weekdays.map((label, index) => (
          <div
            key={`${label}-${index}`}
            className="border-b-2 border-r-2 border-line bg-surface p-1.5 text-center font-mono text-[10px] uppercase tracking-label text-ink-faint"
          >
            {label}
          </div>
        ))}
        {days.map((day) => {
          const iso = toISO(day);
          const inMonth = mode === 'week' || day.getUTCMonth() === anchorMonth;
          const dayItems = itemsForDay(iso);
          const Wrapper = onDayClick ? 'button' : 'div';
          return (
            <Wrapper
              key={iso}
              {...(onDayClick ? { type: 'button', onClick: () => onDayClick(iso) } : {})}
              data-jour={iso}
              data-aujourdhui={iso === today || undefined}
              className={`relative flex min-h-24 flex-col items-start gap-1 border-b-2 border-r-2 border-line p-1.5 text-left ${
                inMonth ? 'bg-paper' : 'bg-surface text-ink-faint'
              }`}
            >
              {iso === today && (
                <span
                  className="absolute inset-x-0 top-0 h-0.5 bg-accent-trait"
                  aria-hidden="true"
                />
              )}
              <span className="font-mono text-[11px]">{day.getUTCDate()}</span>
              <div className="flex w-full flex-col gap-0.5">
                {dayItems.slice(0, maxPerDay).map((item) => renderItem(item))}
                {dayItems.length > maxPerDay && (
                  <span className="text-[10px] text-ink-faint">
                    {t('planning.moreCount', { count: dayItems.length - maxPerDay })}
                  </span>
                )}
              </div>
            </Wrapper>
          );
        })}
      </div>
    </div>
  );
}
